import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error('Ungültiger Reset-Link');
      navigate('/login');
    }
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (password.length < 8) {
      toast.error('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }

    setIsLoading(true);

    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, {
        token,
        new_password: password
      });

      setResetSuccess(true);
      toast.success('Passwort erfolgreich zurückgesetzt! 🎉');
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      console.error('Reset password error:', error);
      const message = error.response?.data?.detail || 'Fehler beim Zurücksetzen des Passworts';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (resetSuccess) {
    return (
      <>
        <style>{`
          .glassmorphic-card {
            background: rgba(255, 255, 255, 0.9);
            backdrop-filter: blur(20px);
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
          }
        `}</style>
        
        <div className="min-h-screen flex items-center justify-center px-4 py-8" style={{ background: 'linear-gradient(180deg, #F9F7F4 0%, #F5F3F0 100%)' }}>
          <div className="glassmorphic-card rounded-3xl p-8 md:p-10 w-full max-w-md border-2 border-white/40 text-center">
            {/* Logo/Brand */}
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold mb-2" style={{ color: '#5B3FFF' }}>AdSimplify</h1>
              <div className="w-16 h-1 bg-gradient-to-r from-indigo-600 to-indigo-400 mx-auto rounded-full"></div>
            </div>

            <div className="mb-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold mb-2" style={{ color: '#1A1A1A' }}>
                Geschafft! ✅
              </h2>
              <p className="text-gray-600">
                Dein Passwort wurde erfolgreich zurückgesetzt.
              </p>
            </div>

            <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-5 mb-6">
              <p className="text-blue-800 text-sm">
                Du wirst in wenigen Sekunden zum Login weitergeleitet...
              </p>
            </div>

            <Link
              to="/login"
              className="inline-block px-8 py-4 rounded-2xl font-bold text-white shadow-lg transition-all duration-300 hover:transform hover:translateY(-2px)"
              style={{ background: 'linear-gradient(135deg, #0040FF 0%, #0033CC 100%)' }}
            >
              Jetzt einloggen
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{`
        .glassmorphic-card {
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(20px);
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
        }
        
        .btn-primary {
          background: linear-gradient(135deg, #0040FF 0%, #0033CC 100%);
          transition: all 0.3s ease;
        }
        
        .btn-primary:hover {
          background-color: #0033CC;
          transform: translateY(-2px);
          box-shadow: 0 8px 16px rgba(0, 64, 255, 0.3);
        }
        
        .input-field {
          transition: all 0.3s ease;
        }
        
        .input-field:focus {
          border-color: #0040FF;
          box-shadow: 0 0 0 3px rgba(0, 64, 255, 0.1);
        }
      `}</style>
      
      <div className="min-h-screen flex items-center justify-center px-4 py-8" style={{ background: 'linear-gradient(180deg, #F9F7F4 0%, #F5F3F0 100%)' }}>
        <div className="absolute top-8 left-8">
          <button 
            onClick={() => navigate('/login')}
            className="flex items-center gap-2 px-5 py-3 rounded-full font-semibold border-2 border-gray-300 text-gray-700 hover:bg-white hover:shadow-lg transition-all duration-300"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück
          </button>
        </div>

        <div className="glassmorphic-card rounded-3xl p-8 md:p-10 w-full max-w-md border-2 border-white/40">
          {/* Logo/Brand */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold mb-2" style={{ color: '#5B3FFF' }}>AdSimplify</h1>
            <div className="w-16 h-1 bg-gradient-to-r from-indigo-600 to-indigo-400 mx-auto rounded-full"></div>
          </div>

          <h2 className="text-3xl font-bold mb-2" style={{ color: '#1A1A1A' }}>
            Neues Passwort setzen
          </h2>
          <p className="text-gray-600 mb-8">
            Wähle ein sicheres, neues Passwort für deinen Account.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="password" className="block text-xs font-bold text-gray-500 uppercase mb-2">
                Neues Passwort *
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mindestens 8 Zeichen"
                className="input-field w-full px-5 py-4 border-2 border-gray-200 rounded-2xl focus:outline-none bg-white/90"
                required
                minLength={8}
                disabled={isLoading}
              />
              <p className="text-xs text-gray-500 mt-2">
                Mindestens 8 Zeichen
              </p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-xs font-bold text-gray-500 uppercase mb-2">
                Passwort bestätigen *
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Passwort wiederholen"
                className="input-field w-full px-5 py-4 border-2 border-gray-200 rounded-2xl focus:outline-none bg-white/90"
                required
                disabled={isLoading}
              />
            </div>

            {password && confirmPassword && password !== confirmPassword && (
              <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4">
                <p className="text-red-800 text-sm font-medium">
                  ⚠️ Passwörter stimmen nicht überein
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || password !== confirmPassword}
              className="btn-primary w-full py-4 px-5 rounded-2xl font-bold text-white shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Wird gespeichert...' : 'Passwort zurücksetzen'}
            </button>

            <div className="text-center pt-4">
              <Link
                to="/login"
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                Abbrechen und <span className="text-indigo-600 font-semibold">zum Login</span>
              </Link>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
